import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../student/Navbar';
import ITSidebar from './ITSidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import {
  FaUsers,
  FaSearch,
  FaFilter,
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaDownload,
  FaUpload,
  FaChevronLeft,
  FaChevronRight,
  FaUserGraduate,
  FaUserMd,
  FaCheck,
  FaTimes,
  FaExclamationTriangle,
  FaInfoCircle,
  FaUserPlus,
  FaFileExcel,
  FaFileCsv,
  FaSync,
  FaBell,
  FaShieldAlt,
  FaDatabase,
  FaServer,
  FaNetworkWired,
  FaLock,
  FaUnlock,
  FaKey,
  FaUserLock,
  FaUserCheck,
  FaUserTimes,
  FaBan,
  FaCheckCircle,
  FaExclamationCircle,
  FaQuestionCircle,
  FaCog as FaSettings,
  FaChartBar,
  FaTable,
  FaList,
  FaTh,
  FaThList,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaExpand,
  FaCompress,
  FaCopy,
  FaClipboard,
  FaPrint,
  FaShare,
  FaExternalLinkAlt,
  FaArrowLeft,
  FaArrowRight,
  FaArrowUp,
  FaArrowDown,
  FaChevronUp,
  FaChevronDown,
  FaMinus,
  FaEquals,
  FaPercentage,
  FaCalculator,
  FaClock,
  FaCalendar,
  FaCalendarAlt,
  FaCalendarCheck,
  FaCalendarTimes,
  FaHistory,
  FaUndo,
  FaRedo,
  FaSave,
  FaTimesCircle,
  FaCheckSquare,
  FaSquare,
  FaCircle,
  FaDotCircle,
  FaStop,
  FaPlay,
  FaPause,
  FaStopCircle,
  FaPlayCircle,
  FaPauseCircle,
  FaForward,
  FaBackward,
  FaStepForward,
  FaStepBackward,
  FaFastForward,
  FaFastBackward,
  FaRandom,
  FaRepeat,
  FaRedoAlt,
  FaUndoAlt,
  FaSyncAlt,
  FaSpinner,
  FaCircleNotch,
  FaHourglassHalf,
  FaHourglassStart,
  FaHourglassEnd,
  FaHourglass,
  FaStopwatch,
  FaClock as FaTime,
  FaCalendarDay,
  FaCalendarWeek,
  FaCalendarMonth,
  FaCalendarYear,
  FaCalendarPlus,
  FaCalendarMinus,
  FaCalendarEdit,
  FaCalendarX,
  FaCalendarAlt as FaCalendarAltIcon,
  FaCalendarCheck as FaCalendarCheckIcon,
  FaCalendarTimes as FaCalendarTimesIcon,
  FaCalendarDay as FaCalendarDayIcon,
  FaCalendarWeek as FaCalendarWeekIcon,
  FaCalendarMonth as FaCalendarMonthIcon,
  FaCalendarYear as FaCalendarYearIcon,
  FaCalendarPlus as FaCalendarPlusIcon,
  FaCalendarMinus as FaCalendarMinusIcon,
  FaCalendarEdit as FaCalendarEditIcon,
  FaCalendarX as FaCalendarXIcon
} from 'react-icons/fa';

// Website color palette
const websiteColorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

const Accounts = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState({});
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Modals and popups
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showEditAccount, setShowEditAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  
  // Form states
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [newAccount, setNewAccount] = useState({
    email: '',
    name: '',
    role: 'student',
    studentId: '',
    universityId: '',
    clinicId: '',
    affiliationType: 'university',
    dentistId: '',
    accountType: 'microsoft'
  });
  const [editAccount, setEditAccount] = useState({});
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [accountDetails, setAccountDetails] = useState(null);
  
  
  // View modes
  const [viewMode, setViewMode] = useState('table'); // table, grid, list
  const [showFilters, setShowFilters] = useState(false);
  
  const navigate = useNavigate();
  const { user, token } = useAuth();

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user || !token) {
        setError('Please log in to view accounts.');
        setLoading(false);
        return;
      }

      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const params = new URLSearchParams({
          page: currentPage,
          limit: 20,
          search: searchTerm,
          role: roleFilter !== 'all' ? roleFilter : ''
        });

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/it/accounts?${params}`,
          config
        );

        setAccounts(response.data.accounts || []);
        setPagination(response.data.pagination || {});
        setTotalPages(response.data.pagination?.totalPages || 1);

      } catch (err) {
        const errorMessage =
          err.response?.status === 404
            ? 'Data endpoint not found.'
            : err.response?.status === 401
            ? 'Unauthorized. Please log in again.'
            : err.response?.data?.message || 'Failed to load accounts';
        setError(errorMessage);
        if (err.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [user, token, navigate, currentPage, searchTerm, roleFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    // The useEffect will handle the search
  };

  const handleRoleFilter = (role) => {
    setRoleFilter(role);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Account Management Functions
  const handleAddAccount = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const cleanedData = { ...newAccount };
      
      // Clean data based on role
      if (newAccount.role !== 'student') delete cleanedData.studentId;
      if (!['student', 'supervisor', 'admin', 'it'].includes(newAccount.role)) delete cleanedData.universityId;
      if (newAccount.role !== 'dentist') delete cleanedData.clinicId;
      if (newAccount.role !== 'assistant') delete cleanedData.affiliationType;
      if (!(newAccount.role === 'assistant' && newAccount.affiliationType === 'dentist')) delete cleanedData.dentistId;
      
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/it/accounts`, cleanedData, config);
      
      setSuccess('Account created successfully!');
      setShowAddAccount(false);
      setNewAccount({
        email: '',
        name: '',
        role: 'student',
        studentId: '',
        universityId: '',
        clinicId: '',
        affiliationType: 'university',
        dentistId: '',
        accountType: 'microsoft'
      });
      
      // Refresh data
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create account');
    }
  };

  const handleEditAccount = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const cleanedData = { ...editAccount };
      
      // Clean data based on role
      if (editAccount.role !== 'student') delete cleanedData.studentId;
      if (!['student', 'supervisor', 'admin', 'it'].includes(editAccount.role)) delete cleanedData.universityId;
      if (editAccount.role !== 'dentist') delete cleanedData.clinicId;
      if (editAccount.role !== 'assistant') delete cleanedData.affiliationType;
      if (!(editAccount.role === 'assistant' && editAccount.affiliationType === 'dentist')) delete cleanedData.dentistId;
      
      await axios.put(`${process.env.REACT_APP_API_URL}/api/it/accounts/${editAccount.accountType || 'student'}/${editAccount.id}`, cleanedData, config);
      
      setSuccess('Account updated successfully!');
      setShowEditAccount(false);
      
      // Refresh data
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update account');
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/it/accounts/${accountToDelete.accountType || 'student'}/${accountToDelete.id}`, config);
      
      setSuccess('Account deleted successfully!');
      setShowDeleteConfirm(false);
      setAccountToDelete(null);
      
      // Refresh data
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    }
  };

  const handleBulkDelete = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/it/accounts/bulk-delete`,
        { accountIds: selectedAccounts },
        config
      );
      
      setSuccess(`Successfully deleted ${response.data.deletedCount} accounts`);
      setShowBulkDelete(false);
      setSelectedAccounts([]);
      
      // Refresh data
      await fetchAccounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete accounts');
    }
  };

  // Account Actions
  const openEditAccount = (account) => {
    setEditAccount(account);
    setShowEditAccount(true);
  };

  const openDeleteConfirm = (account) => {
    setAccountToDelete(account);
    setShowDeleteConfirm(true);
  };

  const openAccountDetails = (account) => {
    setAccountDetails(account);
    setShowAccountDetails(true);
  };

  // Selection handlers
  const toggleAccountSelection = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId) 
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const selectAllAccounts = () => {
    setSelectedAccounts(accounts.map(account => account.id));
  };

  const clearSelection = () => {
    setSelectedAccounts([]);
  };


  // Utility functions
  const fetchAccounts = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const params = new URLSearchParams({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        role: roleFilter !== 'all' ? roleFilter : '',
        status: statusFilter !== 'all' ? statusFilter : '',
        sortBy: sortBy,
        sortOrder: sortOrder
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/it/accounts?${params}`,
        config
      );

      setAccounts(response.data.accounts || []);
      setPagination(response.data.pagination || {});
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      const errorMessage =
        err.response?.status === 404
          ? 'Data endpoint not found.'
          : err.response?.status === 401
          ? 'Unauthorized. Please log in again.'
          : err.response?.data?.message || 'Failed to load accounts';
      setError(errorMessage);
      if (err.response?.status === 401) navigate('/login');
    }
  };

  const refreshData = async () => {
    setLoading(true);
    await fetchAccounts();
    setLoading(false);
  };


  const getRoleColor = (role) => {
    switch (role) {
      case 'student':
        return 'bg-green-100 text-green-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'supervisor':
        return 'bg-purple-100 text-purple-800';
      case 'assistant':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gray-50">
      <ITSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            {/* Enhanced Notifications */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaExclamationCircle className="w-5 h-5 text-red-500 mr-3" />
                    <p className="text-red-700 font-medium">{error}</p>
                  </div>
                  <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                    <FaTimes />
                  </button>
                </div>
              </motion.div>
            )}
            
            {success && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-lg shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaCheckCircle className="w-5 h-5 text-green-500 mr-3" />
                    <p className="text-green-700 font-medium">{success}</p>
                  </div>
                  <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
                    <FaTimes />
                  </button>
                </div>
              </motion.div>
            )}

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
              <div className="mb-6 sm:mb-8">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                  <div>
                    <h1 className={`text-2xl sm:text-3xl md:text-4xl font-bold text-[${websiteColorPalette.primary}] mb-1`}>Account Management</h1>
                    <p className={`text-[${websiteColorPalette.text}]`}>Manage all university accounts</p>
                  </div>
                  
                  <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
                    <button
                      onClick={refreshData}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                    >
                      <FaSync className="h-4 w-4 mr-2" />
                      Refresh
                    </button>
                    <button
                      onClick={() => setShowAddAccount(true)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <FaPlus className="h-4 w-4 mr-2" />
                      Add Account
                    </button>
                  </div>
                </div>
                
                {/* Stats Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center">
                      <FaUsers className="h-8 w-8 text-blue-500 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Accounts</p>
                        <p className="text-2xl font-bold text-gray-900">{pagination.total || 0}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center">
                      <FaUserGraduate className="h-8 w-8 text-green-500 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Students</p>
                        <p className="text-2xl font-bold text-gray-900">{accounts.filter(a => a.role === 'student').length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center">
                      <FaUserMd className="h-8 w-8 text-purple-500 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">Staff</p>
                        <p className="text-2xl font-bold text-gray-900">{accounts.filter(a => ['admin', 'supervisor', 'assistant'].includes(a.role)).length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                    <div className="flex items-center">
                      <FaShieldAlt className="h-8 w-8 text-orange-500 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-600">IT Accounts</p>
                        <p className="text-2xl font-bold text-gray-900">{accounts.filter(a => a.role === 'it').length}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enhanced Search and Filter Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  <form onSubmit={handleSearch} className="flex-1 flex gap-4">
                    <div className="flex-1 relative">
                      <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        placeholder="Search by name, email, or student ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      type="submit"
                      className={`px-6 py-2 bg-[${websiteColorPalette.primary}] text-white rounded-lg hover:bg-blue-700 transition-colors`}
                    >
                      Search
                    </button>
                  </form>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                    >
                      <FaFilter className="h-4 w-4 mr-2" />
                      Filters
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewMode('table')}
                        className={`px-3 py-2 rounded-lg transition-colors ${
                          viewMode === 'table' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title="Table View"
                      >
                        <FaTable className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`px-3 py-2 rounded-lg transition-colors ${
                          viewMode === 'grid' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title="Grid View"
                      >
                        <FaTh className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`px-3 py-2 rounded-lg transition-colors ${
                          viewMode === 'list' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title="List View"
                      >
                        <FaList className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Advanced Filters */}
                {showFilters && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Role Filter</label>
                        <select
                          value={roleFilter}
                          onChange={(e) => setRoleFilter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Roles</option>
                          <option value="student">Students</option>
                          <option value="admin">Admins</option>
                          <option value="supervisor">Supervisors</option>
                          <option value="assistant">Assistants</option>
                          <option value="dentist">Dentists</option>
                          <option value="it">IT</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
                        <select
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="name">Name</option>
                          <option value="email">Email</option>
                          <option value="role">Role</option>
                          <option value="createdAt">Created Date</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
                        <select
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="asc">Ascending</option>
                          <option value="desc">Descending</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Enhanced Accounts Table */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900">
                      Accounts ({pagination.total || 0})
                      {selectedAccounts.length > 0 && (
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                          {selectedAccounts.length} selected
                        </span>
                      )}
                    </h2>
                    <div className="flex gap-2">
                      {selectedAccounts.length > 0 && (
                        <>
                          <button
                            onClick={() => setShowBulkDelete(true)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
                          >
                            <FaTrash className="h-4 w-4 mr-2" />
                            Delete Selected
                          </button>
                          <button
                            onClick={clearSelection}
                            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center"
                          >
                            <FaTimes className="h-4 w-4 mr-2" />
                            Clear Selection
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">
                          <input
                            type="checkbox"
                            checked={selectedAccounts.length === accounts.length && accounts.length > 0}
                            onChange={selectedAccounts.length === accounts.length ? clearSelection : selectAllAccounts}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">Name</th>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">Email</th>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">Role</th>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">University</th>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">Status</th>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">Created</th>
                        <th className="text-left py-3 px-6 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {accounts.map((account, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="py-4 px-6">
                            <input
                              type="checkbox"
                              checked={selectedAccounts.includes(account.id)}
                              onChange={() => toggleAccountSelection(account.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                                <span className="text-sm font-medium text-blue-600">
                                  {account.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{account.name}</p>
                                {account.studentId && (
                                  <p className="text-xs text-gray-500">ID: {account.studentId}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="text-sm text-gray-900">{account.email}</div>
                            <div className="text-sm text-gray-500">
                              {account.accountType === 'google' && 'Google Account'}
                              {account.accountType === 'microsoft' && 'Microsoft Account'}
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              account.role === 'student' ? 'bg-green-100 text-green-800' :
                              account.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                              account.role === 'supervisor' ? 'bg-purple-100 text-purple-800' :
                              account.role === 'assistant' ? 'bg-yellow-100 text-yellow-800' :
                              account.role === 'dentist' ? 'bg-red-100 text-red-800' :
                              account.role === 'it' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {account.role?.charAt(0)?.toUpperCase() + account.role?.slice(1)}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900">{account.university || '-'}</td>
                          <td className="py-4 px-6">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              account.status === 'active' ? 'bg-green-100 text-green-800' :
                              account.status === 'inactive' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {account.status || 'Active'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-500">
                            {account.createdAt ? new Date(account.createdAt.seconds * 1000).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex gap-2">
                              <button 
                                onClick={() => openAccountDetails(account)}
                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                title="View Details"
                              >
                                <FaEye className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => openEditAccount(account)}
                                className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                                title="Edit Account"
                              >
                                <FaEdit className="h-4 w-4" />
                              </button>
                              <button 
                                onClick={() => openDeleteConfirm(account)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                title="Delete Account"
                              >
                                <FaTrash className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {accounts.length === 0 && (
                    <div className="text-center py-8">
                      <FaUsers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No accounts found</p>
                      <p className="text-gray-400 text-sm">Try adjusting your search or filter criteria</p>
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        Showing {((currentPage - 1) * 20) + 1} to {Math.min(currentPage * 20, pagination.total || 0)} of {pagination.total || 0} results
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          <FaChevronLeft className="h-4 w-4" />
                        </button>
                        
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => handlePageChange(page)}
                              className={`px-3 py-2 border rounded-lg ${
                                currentPage === page
                                  ? `bg-[${websiteColorPalette.primary}] text-white border-[${websiteColorPalette.primary}]`
                                  : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                        >
                          <FaChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </main>
      </div>

      {/* Add Account Modal */}
      {showAddAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add New Account</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleAddAccount(); }} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                  <input
                    type="email"
                    value={newAccount.email}
                    onChange={(e) => setNewAccount({...newAccount, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                  <input
                    type="text"
                    value={newAccount.name}
                    onChange={(e) => setNewAccount({...newAccount, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role*</label>
                  <select
                    value={newAccount.role}
                    onChange={(e) => setNewAccount({...newAccount, role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="student">Student</option>
                    <option value="admin">Admin</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="assistant">Assistant</option>
                    <option value="dentist">Dentist</option>
                    <option value="it">IT</option>
                  </select>
                </div>
                {newAccount.role === 'student' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Student ID*</label>
                    <input
                      type="text"
                      value={newAccount.studentId}
                      onChange={(e) => setNewAccount({...newAccount, studentId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}
                {['student', 'supervisor', 'admin', 'it'].includes(newAccount.role) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">University*</label>
                    <input
                      type="text"
                      value={newAccount.universityId}
                      onChange={(e) => setNewAccount({...newAccount, universityId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}
                {newAccount.role === 'dentist' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clinic ID*</label>
                    <input
                      type="text"
                      value={newAccount.clinicId}
                      onChange={(e) => setNewAccount({...newAccount, clinicId: e.target.value})}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddAccount(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {showEditAccount && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Account</h2>
            <form onSubmit={(e) => { e.preventDefault(); handleEditAccount(); }} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                  <input
                    type="email"
                    value={editAccount.email}
                    onChange={(e) => setEditAccount({...editAccount, email: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                  <input
                    type="text"
                    value={editAccount.name}
                    onChange={(e) => setEditAccount({...editAccount, name: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role*</label>
                  <select
                    value={editAccount.role}
                    onChange={(e) => setEditAccount({...editAccount, role: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="student">Student</option>
                    <option value="admin">Admin</option>
                    <option value="supervisor">Supervisor</option>
                    <option value="assistant">Assistant</option>
                    <option value="dentist">Dentist</option>
                    <option value="it">IT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={editAccount.status || 'active'}
                    onChange={(e) => setEditAccount({...editAccount, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditAccount(false)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Update Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <FaExclamationTriangle className="h-6 w-6 text-red-500 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Delete Account</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the account for <strong>{accountToDelete?.name}</strong>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <FaExclamationTriangle className="h-6 w-6 text-red-500 mr-3" />
              <h2 className="text-xl font-bold text-gray-900">Delete Selected Accounts</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{selectedAccounts.length}</strong> selected accounts? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowBulkDelete(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete {selectedAccounts.length} Accounts
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Account Details Modal */}
      {showAccountDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Account Details</h2>
              <button
                onClick={() => setShowAccountDetails(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            {accountDetails && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <p className="text-gray-900">{accountDetails.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="text-gray-900">{accountDetails.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <p className="text-gray-900">{accountDetails.role}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <p className="text-gray-900">{accountDetails.status || 'Active'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">University</label>
                    <p className="text-gray-900">{accountDetails.university || '-'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Login</label>
                    <p className="text-gray-900">
                      {accountDetails.lastLogin ? new Date(accountDetails.lastLogin).toLocaleString() : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Accounts;

